import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  Copy,
  Delete,
  Download,
  Eye,
  EyeIcon,
  Filter,
  Instagram,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Search,
  Send,
  Twitter,
  Users,
  XCircle,
} from "lucide-react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PlatformUser } from "../../App";
import useDebounce from "../../hooks/useDebounce";
import { ApiError, Refferal } from "../../services/api/auth/auth.type";
import {
  useGetTransactionsApiCallQuery,
  useLazyGetTransactionsApiCallQuery,
} from "../../services/api/transactions/transactions.api";
import { User } from "../../services/api/users/user.type";
import {
  useChangeUsersStatusApiCallMutation,
  useDeleteUsersStatusApiCallMutation,
  useGetUsersAnalyticsApiCallQuery,
  useAddKolMutation,
  useGetUsersApiCallQuery,
  useLazyGetUsersApiCallQuery,
} from "../../services/api/users/users.api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  useGetRefferalsApiCallQuery,
  useLazyGetRefferalsApiCallQuery,
} from "../../services/api/auth/auth.api";
import { TransactionsType } from "../../services/api/transactions/transactions.type";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { set } from "react-hook-form";
import shortenAddress from "../../utils/shortenAddress";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useGetTokenPriceConversionApiCallQuery } from "../../services/api/campaigns/campaigns.api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Crown } from "lucide-react";

// Mock user data
const mockUsers: PlatformUser[] = [
  {
    id: "1",
    walletAddress: "0x1234567890abcdef1234567890abcdef12345678",
    joinedDate: "2024-01-15T10:30:00Z",
    linkedAccounts: {
      twitter: "@user1",
      telegram: "@user1_tg",
    },
    campaignsJoined: 3,
    status: "active",
    totalRewards: 450,
  },
  {
    id: "2",
    walletAddress: "0xabcdef1234567890abcdef1234567890abcdef12",
    joinedDate: "2024-01-16T14:20:00Z",
    linkedAccounts: {
      twitter: "@cryptouser2",
    },
    campaignsJoined: 2,
    status: "active",
    totalRewards: 320,
  },
  {
    id: "3",
    walletAddress: "0x9876543210fedcba9876543210fedcba98765432",
    joinedDate: "2024-01-17T09:15:00Z",
    linkedAccounts: {
      telegram: "@suspicious_user",
    },
    campaignsJoined: 1,
    status: "flagged",
    totalRewards: 100,
  },
  {
    id: "4",
    walletAddress: "0xfedcba9876543210fedcba9876543210fedcba98",
    joinedDate: "2024-01-18T16:45:00Z",
    linkedAccounts: {
      twitter: "@banned_user",
      telegram: "@banned_user_tg",
    },
    campaignsJoined: 5,
    status: "banned",
    totalRewards: 0,
  },
  {
    id: "5",
    walletAddress: "0x1111222233334444555566667777888899990000",
    joinedDate: "2024-01-19T11:30:00Z",
    linkedAccounts: {
      twitter: "@activeuser5",
      telegram: "@activeuser5_tg",
    },
    campaignsJoined: 4,
    status: "active",
    totalRewards: 675,
  },
];

// Mock user activity data
const mockUserActivity = [
  {
    id: "1",
    campaignName: "Project X Launch",
    taskType: "twitter_follow",
    taskDescription: "Follow @ProjectX_Official",
    status: "completed",
    completedDate: "2024-01-16T10:30:00Z",
    reward: 100,
  },
  {
    id: "2",
    campaignName: "Project X Launch",
    taskType: "twitter_retweet",
    taskDescription: "Retweet launch announcement",
    status: "completed",
    completedDate: "2024-01-16T11:15:00Z",
    reward: 50,
  },
  {
    id: "3",
    campaignName: "Community Growth",
    taskType: "telegram_join",
    taskDescription: "Join community channel",
    status: "pending",
    completedDate: null,
    reward: 75,
  },
  {
    id: "4",
    campaignName: "Beta Testing",
    taskType: "twitter_like",
    taskDescription: "Like beta announcement",
    status: "failed",
    completedDate: null,
    reward: 25,
  },
];

const AddKolButton: React.FC<{ userId: string }> = ({ userId }) => {
  const [addKol, { isLoading }] = useAddKolMutation();

  const handleAddKol = async () => {
    try {
      const data = await addKol(userId).unwrap();
      toast.success(data.responseMessage, {
        icon: <Crown className="h-4 w-4" />,
      });
    } catch (error: any) {
      toast.error("Failed to promote user to KOL", {
        description: error?.data?.message || "Something went wrong",
      });
    }
  };

  return (
    <AlertDialogAction
      onClick={handleAddKol}
      disabled={isLoading}
      className="bg-purple-600 hover:bg-purple-700"
    >
      {isLoading ? "Promoting..." : "Confirm & Add as KOL"}
    </AlertDialogAction>
  );
};

const UserManagement: React.FC = () => {
  // use states
  const [users, setUsers] = useState<PlatformUser[]>(mockUsers);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [campaignFilter, setCampaignFilter] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selected, setSelected] = useState<Refferal | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const [activityScroll, setActivityScroll] = useState(false);

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const [refferalsLists, setRefferalsLists] = useState<Refferal[]>([]);
  const [transactionsLists, setTransactionsLists] = useState<
    TransactionsType[]
  >([]);

  const [referralTransactions, setReferralTransactions] = useState<
    TransactionsType[]
  >([]);

  const [page, setPage] = useState(1);

  const [refferalsPage, setRefferalsPage] = useState(1);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const refferalRef = useRef<HTMLDivElement | null>(null);
  const transactionsRef = useRef<HTMLDivElement>(document.createElement("div"));

  const debouncedSearch = useDebounce(searchTerm, 500);

  // api call
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isError,
  } = useGetUsersApiCallQuery({
    search: debouncedSearch || "",
    status: statusFilter === "all" ? "" : statusFilter || "",
    page: page,
    limit: 10,
  });

  const [getUsers] = useLazyGetUsersApiCallQuery();

  const { data: analayticsUsers } = useGetUsersAnalyticsApiCallQuery();
  // const { data: transactions } = useGetTransactionsApiCallQuery({
  //   transactionType: "CLAIMED",
  //   page: 1,
  //   limit: 10,
  // });

  // const { data: refferals } = useGetRefferalsApiCallQuery({
  //   page: 1,
  //   limit: 10,
  //   userId: selectedUser?._id || "",
  // });

  const [getTransactions, { data: transactions }] =
    useLazyGetTransactionsApiCallQuery();

  const [getRefferals, { data: refferals }] = useLazyGetRefferalsApiCallQuery();

  const [deleteUser] = useDeleteUsersStatusApiCallMutation();
  const [changeUserStatus] = useChangeUsersStatusApiCallMutation();

  const getStatusBadge = (status: string) => {
    const colors = {
      active: "bg-green-100 text-green-800",
      block: "bg-red-100 text-red-800",
    };

    return (
      <Badge className={colors[status.toLowerCase() as keyof typeof colors]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.walletAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.linkedAccounts.twitter
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      user.linkedAccounts.telegram
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || user.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = async (user: User) => {
    setSelectedUser(user);
    setShowUserDetails(true);

    try {
      const response = await getRefferals({
        userId: user._id,
        page: refferalsPage,
        limit: 10,
      }).unwrap();

      setRefferalsLists(response.result?.docs || []);
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      console.log(errorData);
      setRefferalsLists([]);
      // toast.error(errorData?.responseMessage || "Login failed");
    }
  };

  const handleViewTransactions = async (user: User) => {
    setActivityScroll(true);
    console.log("aaya");
    try {
      const transactionsResponse = await getTransactions({
        userId: user._id,
        page: transactionsPage,
        limit: 4,
      }).unwrap();

      setTransactionsLists(transactionsResponse.result?.docs || []);
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      console.log(errorData);
      setTransactionsLists([]);
      // toast.error(errorData?.responseMessage || "Login failed");
    }
  };

  const formatWalletAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const handleCopy = (address: any) => {
    if (address) {
      navigator.clipboard.writeText(address);
      alert("Wallet address copied!");
    }
  };
  const getTaskStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "pending":
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const handleChangeStatus = async (userId: string) => {
    try {
      const data = await changeUserStatus({
        userId,
      }).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(
        errorData?.responseMessage || "Error changing campaign status"
      );
    }
  };

  const confirmDelete = async () => {
    setIsDeleteLoading(true);
    try {
      const data = await deleteUser({
        userId: deleteTargetId as string,
      }).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        setDeleteTargetId(null);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting campaign");
    } finally {
      setIsDeleteLoading(false);
      setDeleteTargetId(null);
    }
  };

  const handleReferralSelect = async (activity: Refferal) => {
    setSelected(activity);
    try {
      const response = await getTransactions({
        userId: activity._id,
        page: 1,
        limit: 10,
        transactionType: "CLAIMED",
      }).unwrap();
      if (response?.responseCode === 200) {
        setReferralTransactions(response.result?.docs || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleActivityScroll = async () => {
    try {
      const response = await getTransactions({
        userId: selectedUser?._id || "",
        page: transactionsPage,
        limit: 5,
      }).unwrap();

      if (response?.responseCode === 200) {
        const newDocs = response?.result?.docs || [];

        setTransactionsLists((prev) => [...newDocs]);
      }
    } catch (error) {
      console.error("Error fetching more transactions:", error);
    }
  };

  useEffect(() => {
    if (selectedUser?._id) {
      handleActivityScroll();
    }
  }, [transactionsPage]);

  const exportCSV = async () => {
    try {
      setIsExporting(true);

      const response = await getUsers({
        limit: usersData?.result?.total,
      }).unwrap();

      // const data: Transaction[] = response?.result?.data || [];

      if (response.result?.docs.length === 0) {
        toast.info("No data available to export");
        return;
      }

      const exportData = response?.result?.docs?.map((user) => ({
        ID: user._id,
        Name: user.firstName,
        Email: user.email,
        "User Type": user.userType,
        Status: user.status,
        Balance: user.balance,
        "OTP Verified": user.otpVerified ? "Yes" : "No",
        "Google 2FA": user.google2FA ? "Enabled" : "Disabled",
        Permissions: user.permissions?.join(", ") || "-",
        "Low Balance Alert": user.lowBalanceAlert ? "Yes" : "No",
        "Campaign End Alert": user.campaignEndAlert ? "Yes" : "No",
        // "New Campaign Alert": user.newCampaignAlert ? "Yes" : "No",
        // "Claim Reminder": user.claimReminder ? "Yes" : "No",
        // "Social Updates": user.socialUpdates ? "Yes" : "No",
        // "Social Email": user.socialEmail ? "Yes" : "No",
        "KOL Status": user.isKol ? "Active" : "InActive",

        "Total Campaigns": user.totalCampaigns,
        "Total Claimed": user.totalClaimed,
        "Wallet Address": user.walletAddress || "-",
        "Created At": new Date(user.createdAt).toLocaleString(),
        "Updated At": new Date(user.updatedAt).toLocaleString(),
      }));

      // Convert JSON → worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData!);

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "User");

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `user__${Date.now()}.xlsx`);
      toast.success("Excel file exported successfully");
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = (fetchError?.data as ApiError) || {};
      toast.error(errorData?.responseMessage || "Error exporting file");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    // if (refferals?.result?.docs) {
    //   if (refferalsPage === 1) {
    //     setRefferalsLists(refferals?.result?.docs);
    //   } else {
    //     setRefferalsLists([...refferalsLists, ...refferals?.result?.docs]);
    //   }
    // }
  }, [refferalsLists, refferalsPage]);

  useEffect(() => {
    const div = refferalRef.current;

    if (!div) return;

    const handleScroll = () => {
      if (!div) return;

      const { scrollTop, scrollHeight, clientHeight } = div;
      console.log(scrollHeight, scrollTop, clientHeight);

      if (scrollTop + clientHeight >= scrollHeight - 5) {
        console.log("aaya");
        if (refferals?.result?.page) {
          setRefferalsPage((prev) => prev + 1);
        }
      }
    };

    div.addEventListener("scroll", handleScroll);
    console.log("Scroll listener added ✅");

    // return () => {
    //   div.removeEventListener("scroll", handleScroll);
    //   console.log("Scroll listener removed ❌");
    // };
  }, [selectedUser, transactionsRef.current]);

  // useLayoutEffect(() => {
  //   console.log("aaya");
  //   console.log(transactionsRef);
  //   if (!transactionsRef.current) return; // ✅ ensure ref exists

  //   console.log("inref");
  //   const div = transactionsRef.current;

  //   console.log(div);

  //   const handleScroll = async () => {
  //     if (!div) return;

  //     const { scrollTop, scrollHeight, clientHeight } = div;
  //     console.log(scrollHeight, scrollTop, clientHeight);

  //     if (scrollTop + clientHeight >= scrollHeight - 5) {
  //       if (transactions?.result?.page) {
  //         // setTransactionsPage((prev) => prev + 1);

  //         try {
  //           const response = await getTransactions({
  //             userId: selectedUser?._id || "",
  //             page: transactionsPage + 1,
  //             limit: 4,
  //           }).unwrap();

  //           if (response?.responseCode === 200) {
  //             setTransactionsLists([
  //               ...transactionsLists,
  //               ...(response?.result?.docs || []),
  //             ]);
  //             setTransactionsPage((prev) => prev + 1);
  //           }
  //         } catch (error) {}
  //       }
  //     }
  //   };

  //   div.addEventListener("scroll", handleScroll);
  //   console.log("Scroll listener added ✅");

  //   return () => {
  //     div.removeEventListener("scroll", handleScroll);
  //     console.log("Scroll listener removed ❌");
  //   };
  // }, [activityScroll, selectedUser]);

  const getSocialMediaIcon = (platform: string) => {
    switch (platform) {
      case "Twitter":
        return <Twitter className="h-4 w-4" color="#1DA1F2" />;
      case "Telegram":
        return <Send className="h-4 w-4" />;

      case "Instagram":
        return <Instagram className="h-4 w-4" color="#E1306C" />;
      default:
        return null;
    }
  };

  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            User Management
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor and manage platform users
          </p>
        </div>

        <Button
          disabled={isExporting}
          variant="outline"
          className="cursor-pointer"
          onClick={exportCSV}
        >
          <Download className="h-4 w-4 mr-2" />
          Export Users
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-semibold">
                  {analayticsUsers?.result?.totalUser || "-"}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Users</p>
                <p className="text-2xl font-semibold">
                  {/* {users.filter((u) => u.status === "active").length} */}
                  {analayticsUsers?.result?.activeUser || "-"}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Blocked Users</p>
                <p className="text-2xl font-semibold">
                  {/* {users.filter((u) => u.status === "flagged").length} */}
                  {analayticsUsers?.result?.blockUser || "-"}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        {/* <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Rewards</p>
                <p className="text-2xl font-semibold">
                  {users
                    .reduce((sum, u) => sum + u.totalRewards, 0)
                    .toLocaleString()}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card> */}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by wallet, Twitter, or Telegram..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="BLOCK">Block</SelectItem>
                  {/* <SelectItem value="flagged">Flagged</SelectItem> */}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Linked Accounts</TableHead>
                  <TableHead>Campaigns</TableHead>
                  <TableHead>Total Rewards</TableHead>
                  <TableHead>Total Rewards USDT</TableHead>
                  <TableHead>KOL Status</TableHead>

                  <TableHead>Status</TableHead>
                  <TableHead>Joined Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isUsersLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : (
                  !isError &&
                  usersData?.result?.docs &&
                  usersData?.result?.docs?.length > 0 &&
                  usersData.result.docs.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        <div className="full-w font-mono text-sm">
                          {formatWalletAddress(user.walletAddress)}
                          {user.walletAddress ? (
                            <Copy
                              className=" h-4 cursor-pointer text-gray-500 hover:text-black"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  user.walletAddress
                                );
                                toast.success("Wallet address copied!");
                              }}
                            />
                          ) : (
                            <span>--</span>
                          )}{" "}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {user?.allSOcialMedia?.map((media) => (
                            <span>{getSocialMediaIcon(media.platform)}</span>
                          ))}
                          {user?.email && <Mail className="h-4 w-4" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          {user.totalCampaigns}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          {user.totalClaimed ? user.totalClaimed.toFixed(3) : 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          $
                          {user.totalClaimed
                            ? getUSDTBalance(user.totalClaimed).toFixed(3)
                            : 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {user.isKol ? (
                          <div className="group relative inline-block">
                            <Badge
                              variant="outline"
                              className="bg-gradient-to-r from-yellow-100 to-amber-100 text-amber-800 border-amber-300"
                            >
                              <Crown className="h-3.5 w-3.5 mr-1" />
                              KOL
                            </Badge>
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white group-hover:block">
                              Key Opinion Leader – Higher rewards & priority
                            </span>
                          </div>
                        ) : (
                          <Badge variant="secondary" className="text-gray-500">
                            —
                          </Badge>
                        )}
                      </TableCell>

                      <TableCell>{getStatusBadge(user.status)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={(e) => e.preventDefault()} // Prevent dropdown from closing
                              className="text-purple-600 font-medium"
                            >
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button className="flex w-full items-center px-2 py-1.5 text-left text-sm">
                                    {user.isKol ? (
                                      <>
                                        <Crown className="h-4 w-4 mr-2 text-purple-600" />
                                        Remove KOL Status
                                      </>
                                    ) : (
                                      <>
                                        <Crown className="h-4 w-4 mr-2 text-gray-400" />
                                        Add as KOL
                                      </>
                                    )}
                                  </button>
                                </AlertDialogTrigger>

                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="flex items-center gap-2">
                                      <Crown
                                        className={`h-5 w-5 ${
                                          user.isKol
                                            ? "text-red-500"
                                            : "text-purple-600"
                                        }`}
                                      />
                                      {user.isKol
                                        ? "Remove KOL Status"
                                        : "Promote to KOL"}
                                    </AlertDialogTitle>
                                    <AlertDialogDescription className="space-y-3">
                                      <p>
                                        {user.isKol
                                          ? "This will revoke the user's KOL privileges."
                                          : "This user will be promoted to KOL status."}
                                      </p>
                                      <p className="text-sm">
                                        {user.isKol
                                          ? "They will lose the KOL badge and any associated benefits."
                                          : "They will receive a KOL badge, higher rewards, and priority access."}
                                      </p>
                                      <div className="font-mono text-sm bg-muted px-2 py-1 rounded inline-block">
                                        {formatWalletAddress(
                                          user.walletAddress
                                        )}
                                      </div>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>

                                  <AlertDialogFooter>
                                    <AlertDialogCancel>
                                      Cancel
                                    </AlertDialogCancel>
                                    <AddKolButton userId={user._id} />
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                handleViewDetails(user);
                                handleViewTransactions(user);
                                setTransactionsPage(1);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {user.status.toLowerCase() !== "active" ? (
                              <DropdownMenuItem
                                onClick={() => handleChangeStatus(user._id)}
                                className="text-green-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Active
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => handleChangeStatus(user._id)}
                                className="text-red-600"
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                Block
                              </DropdownMenuItem>
                            )}

                            <DropdownMenuItem
                              onClick={() => setDeleteTargetId(user._id)}
                              className="text-red-600 cursor-pointer"
                            >
                              <Delete className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {usersData?.result && usersData.result.pages > 1 && (
              <div className="flex justify-end items-center mt-4 gap-3">
                {/* ---------- Previous ---------- */}
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>

                {/* ---------- Page Indicator + Input ---------- */}
                <div className="flex items-center gap-1 text-sm">
                  <span>Page</span>

                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="w-12 border rounded text-center focus:outline-none focus:ring-2 focus:ring-primary"
                    value={page}
                    onChange={(e) => {
                      const raw = Number(e.target.value.trim());

                      setPage(raw);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        (e.target as HTMLInputElement).blur();
                    }}
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      if (raw === "") return setPage(1);

                      const n = Number(raw);
                      const totalPages = usersData?.result?.pages || 1;
                      if (Number.isNaN(n) || n < 1) {
                        setPage(1);
                      } else if (n > totalPages) {
                        setPage(totalPages);
                      } else {
                        setPage(n);
                      }
                    }}
                    aria-label="Current page"
                  />

                  <span>of {usersData.result.pages}</span>
                </div>

                {/* ---------- Next ---------- */}
                <Button
                  variant="outline"
                  disabled={page === usersData.result.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}

            {/* popup for delete confirmation  */}
            {deleteTargetId && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-xl max-w-sm w-full">
                  <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    Confirm Delete
                  </h2>
                  <p className="mb-6 text-gray-600 dark:text-gray-300">
                    Are you sure you want to delete this campaign?
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => setDeleteTargetId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                      onClick={confirmDelete}
                    >
                      Yes, Delete{" "}
                      {isDeleteLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {(isError || usersData?.result?.docs.length === 0) && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No users found
              </h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filter criteria"
                  : "No users have joined the platform yet"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={showUserDetails} onOpenChange={setShowUserDetails}>
        <DialogContent className="max-w-6xl w-full max-h-[80vh] overflow-y-hidden">
          <DialogHeader>
            <DialogTitle>User Detais</DialogTitle>
            <DialogDescription>
              Detailed information and activity log for{" "}
              {selectedUser && formatWalletAddress(selectedUser.walletAddress)}
            </DialogDescription>

            {/* <button onClick={() => setActivityScroll(!activityScroll)}>
              click
            </button> */}
          </DialogHeader>

          {selectedUser && (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger
                  value="activity"
                  onClick={() => setActivityScroll(true)}
                >
                  Activity Log
                </TabsTrigger>
                <TabsTrigger value="refferal">Refferal</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">
                        Account Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Wallet Address
                        </p>
                        <p className=" flex font-mono text-sm break-all">
                          {formatWalletAddress(selectedUser.walletAddress)}
                          {selectedUser && selectedUser.walletAddress ? (
                            <Copy
                              className=" h-4 cursor-pointer text-gray-500 hover:text-black ml-2"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  selectedUser.walletAddress
                                );
                                toast.success("Wallet address copied!");
                              }}
                            />
                          ) : (
                            <span>--</span>
                          )}{" "}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Status
                        </p>
                        {getStatusBadge(selectedUser.status)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Joined Date
                        </p>
                        <p className="text-sm">
                          {new Date(selectedUser.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">
                        Platform Activity
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Campaigns Joined
                        </p>
                        <p className="text-sm font-bold">
                          {new Date(selectedUser.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Total Rewards
                        </p>
                        <p className="text-2xl font-bold">
                          {selectedUser.totalClaimed}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Total Rewards USDT
                        </p>
                        <p className="text-2xl font-bold">
                          $
                          {selectedUser.totalClaimed
                            ? getUSDTBalance(selectedUser.totalClaimed).toFixed(
                                5
                              )
                            : 0}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">
                      Linked Social Accounts
                    </CardTitle>
                  </CardHeader>
                  {selectedUser?.allSOcialMedia?.length > 0 &&
                    selectedUser?.allSOcialMedia?.map((item) => (
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                            {/* <Twitter className="h-5 w-5 text-blue-500" /> */}
                            {getSocialMediaIcon(item?.platform)}
                            <div>
                              <p className="font-medium">{item?.platform}</p>
                              <p className="text-sm text-gray-600">
                                {item?.name || item?.username}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    ))}

                  {selectedUser?.email && (
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                          {/* <Twitter className="h-5 w-5 text-blue-500" /> */}
                          <div>
                            <p className="font-medium">Email</p>
                            <p className="text-sm text-gray-600">
                              {selectedUser?.email || "--"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="activity" className="space-y-4">
                <Card className="">
                  <CardHeader>
                    <CardTitle className="text-base">
                      Task Completion History
                    </CardTitle>
                    <CardDescription>
                      All tasks attempted by this user across all campaigns
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div
                      className="space-y-4 max-h-72 overflow-y-scroll"
                      // ref={transactionsRef}
                      // ref={transactionsRef}
                      // onScroll={handleActivityScroll}
                    >
                      {transactions?.result && transactionsLists?.length > 0 ? (
                        transactionsLists.map((activity) => (
                          <div
                            key={activity._id}
                            className="flex items-start space-x-4 p-4 border rounded-lg"
                          >
                            <div className="mt-1">
                              {getTaskStatusIcon(activity?.status)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="font-medium text-gray-900">
                                  {activity.campaignId?.name}
                                </h4>
                                <Badge variant="outline">
                                  {activity?.amount} tokens
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">
                                {/* {activity.taskDescription} */}
                              </p>
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>
                                  Status:{" "}
                                  <span
                                    className={`font-medium ${
                                      activity.status === "COMPLETED"
                                        ? "text-green-600"
                                        : activity.status === "failed"
                                        ? "text-red-600"
                                        : "text-yellow-600"
                                    }`}
                                  >
                                    {activity.status}
                                  </span>
                                </span>
                                {activity.createdAt && (
                                  <span>
                                    Completed:{" "}
                                    {new Date(
                                      activity.createdAt
                                    ).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div>
                          <p className="text-sm text-gray-600 mb-2">
                            No transactions found
                          </p>
                        </div>
                      )}

                      {transactions?.result &&
                        transactions.result.pages > 1 && (
                          <div className="flex justify-end items-center mt-4 gap-3">
                            {/* ---------- Previous ---------- */}
                            <Button
                              variant="outline"
                              disabled={transactionsPage === 1}
                              onClick={() => setTransactionsPage((p) => p - 1)}
                            >
                              Previous
                            </Button>

                            {/* ---------- Page Indicator + Input ---------- */}
                            <div className="flex items-center gap-1 text-sm">
                              <span>Page</span>

                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className="w-12 border rounded text-center focus:outline-none focus:ring-2 focus:ring-primary"
                                value={transactionsPage}
                                onChange={(e) => {
                                  const raw = Number(e.target.value.trim());

                                  setTransactionsPage(raw);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    (e.target as HTMLInputElement).blur();
                                }}
                                onBlur={(e) => {
                                  const raw = e.target.value.trim();
                                  if (raw === "") return setTransactionsPage(1);

                                  const n = Number(raw);
                                  const totalPages =
                                    transactions?.result?.pages || 1;
                                  if (Number.isNaN(n) || n < 1) {
                                    setTransactionsPage(1);
                                  } else if (n > totalPages) {
                                    setTransactionsPage(totalPages);
                                  } else {
                                    setTransactionsPage(n);
                                  }
                                }}
                                aria-label="Current page"
                              />

                              <span>of {transactions.result.pages}</span>
                            </div>

                            {/* ---------- Next ---------- */}
                            <Button
                              variant="outline"
                              disabled={
                                transactionsPage === transactions.result.pages
                              }
                              onClick={() => setTransactionsPage((p) => p + 1)}
                            >
                              Next
                            </Button>
                          </div>
                        )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="refferal" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Refferals</CardTitle>
                    {/* <CardDescription>
                        All tasks attempted by this user across all campaigns
                      </CardDescription> */}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4" ref={refferalRef}>
                      {refferalsLists && refferalsLists?.length > 0 ? (
                        refferalsLists?.map((activity) => (
                          <div
                            key={activity._id}
                            className="flex items-start justify-between space-x-4 p-4 border rounded-lg"
                          >
                            <div className="space-y-1">
                              <p>
                                {" "}
                                <b>Wallet Address</b> :{" "}
                                {shortenAddress(activity?.walletAddress)}
                              </p>
                              <p>
                                {" "}
                                <b>Total Claimed</b> : {activity?.totalClaimed}
                              </p>
                              <p>
                                <b>Date : </b>
                                {new Date(
                                  activity?.createdAt
                                ).toLocaleDateString()}
                              </p>
                            </div>

                            <div
                              className="cursor-pointer"
                              onClick={() => {
                                handleReferralSelect(activity);
                              }}
                            >
                              <EyeIcon size={16} />
                            </div>
                          </div>
                        ))
                      ) : (
                        <div>
                          <p className="text-sm text-gray-600 mb-2">
                            No Refferal Found
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Dialog
                  open={!!selected}
                  onOpenChange={() => setSelected(null)}
                >
                  <DialogContent className="w-full mx-auto h-[360px] flex flex-col">
                    <DialogHeader>
                      <DialogTitle>Referral Details</DialogTitle>
                    </DialogHeader>

                    {selected && (
                      <div className="space-y-3">
                        <p>
                          <strong>Wallet Address : </strong>{" "}
                          {shortenAddress(selected?.walletAddress, 16, 4)}
                        </p>

                        <div className="grid grid-cols-2 gap-6">
                          <div className="border p-4 border-gray-200 rounded rounded-md shadow">
                            <p className="font-semibold text-gray-700">
                              Total Claimed
                            </p>
                            <span>{selected?.totalClaimed}</span>
                          </div>

                          <div className="border p-4 border-gray-200 rounded rounded-md shadow">
                            <p className="font-semibold text-gray-700">
                              Total Campaigns Claimed
                            </p>
                            <span>{selected?.totalCampaignsClaimed}</span>
                          </div>
                        </div>

                        <div className="space-y-4 max-h-72 overflow-y-scroll">
                          {referralTransactions &&
                          referralTransactions?.length > 0 ? (
                            referralTransactions.map((activity) => (
                              <div
                                key={activity._id}
                                className="flex items-start space-x-4 p-4 border rounded-lg"
                              >
                                <div className="mt-1">
                                  {getTaskStatusIcon(activity?.status)}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <h4 className="font-medium text-gray-900">
                                      {activity.campaignId?.name}
                                    </h4>
                                    <Badge variant="outline">
                                      {activity?.amount} tokens
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-2">
                                    {/* {activity.taskDescription} */}
                                  </p>
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <span>
                                      Status:{" "}
                                      <span
                                        className={`font-medium ${
                                          activity.status === "COMPLETED"
                                            ? "text-green-600"
                                            : activity.status === "failed"
                                            ? "text-red-600"
                                            : "text-yellow-600"
                                        }`}
                                      >
                                        {activity.status}
                                      </span>
                                    </span>
                                    {activity.createdAt && (
                                      <span>
                                        Completed:{" "}
                                        {new Date(
                                          activity.createdAt
                                        ).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div>
                              <p className="text-sm text-gray-600 mb-2">
                                No transactions found
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
